/**
 * PlanChangeModal - Modal de changement de plan AI Credits
 * 
 * Gère les transitions entre plans avec validation backend et recommandations.
 * Affiche les détails de la transition (upgrade/downgrade) et guide l'utilisateur.
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import PlansService from '../../services/api/plansService';

interface PlanChangeModalProps {
    id: string;
    currentPlan: string;
    companyId: string;
    accessToken: string;
    onClose?: () => void;
    onSuccess?: () => void; // Callback après changement réussi
}

const PlanChangeModal: React.FC<PlanChangeModalProps> = ({
    id,
    currentPlan,
    companyId,
    accessToken,
    onClose,
    onSuccess
}) => {
    const { t } = useTranslation();
    const closeRef = useRef<HTMLButtonElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    
    const [loading, setLoading] = useState(true);
    const [availablePlans, setAvailablePlans] = useState<any>(null);
    const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
    const [processing, setProcessing] = useState(false);

    // Charger les plans disponibles depuis le backend
    useEffect(() => {
        const loadAvailablePlans = async () => {
            setLoading(true);
            try {
                const response = await PlansService.getAvailablePlans(companyId, accessToken);
                if (response.success && response.data) {
                    setAvailablePlans(response.data);
                } else {
                    console.error('[PlanChangeModal] Failed to load available plans:', response);
                }
            } catch (error) {
                console.error('[PlanChangeModal] Error loading available plans:', error);
            } finally {
                setLoading(false);
            }
        };

        loadAvailablePlans();
    }, [companyId, accessToken]);

    const handleConfirmChange = async () => {
        if (!selectedPlan) return;

        setProcessing(true);
        try {
            const changeReason = getPlanTransitionType(currentPlan, selectedPlan);
            const response = await PlansService.changePlan(
                companyId,
                selectedPlan,
                changeReason,
                null, // Note optionnelle
                accessToken
            );

            if (response.success && response.data) {
                const { action, session_url, steps } = response.data;

                if (action === 'redirect_checkout') {
                    // Rediriger vers Stripe Checkout
                    window.location.href = session_url;
                } else if (action === 'subscription_cancel_required') {
                    // Afficher instructions pour annulation PLUS → ESSENTIAL
                    alert(
                        'Pour passer de PLUS à ESSENTIAL, vous devez d\'abord annuler votre subscription PLUS.\n\n' +
                        'Étapes:\n' +
                        steps.join('\n')
                    );
                    if (closeRef.current) closeRef.current.click();
                    if (onClose) onClose();
                } else if (action === 'manual') {
                    // ENTERPRISE nécessite contact
                    alert('Le plan ENTERPRISE nécessite un contact avec notre équipe. Vous allez être redirigé vers notre formulaire de contact.');
                    window.location.href = '/contact';
                } else {
                    // Changement réussi sans redirection
                    alert('Plan changé avec succès !');
                    if (onSuccess) onSuccess();
                    if (closeRef.current) closeRef.current.click();
                    if (onClose) onClose();
                }
            } else {
                const errorMsg = PlansService.formatErrorMessage(response);
                alert(`Erreur lors du changement de plan: ${errorMsg}`);
            }
        } catch (error) {
            console.error('[PlanChangeModal] Change plan error:', error);
            alert('Erreur lors du changement de plan. Veuillez réessayer.');
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose();
        }
        document.body.style.overflow = '';
    }, [onClose]);

    useEffect(() => {
        const modalElement = modalRef.current;
        if (modalElement) {
            const handleModalHidden = () => {
                handleClose();
            };
            modalElement.addEventListener('hidden.bs.modal', handleModalHidden);
            document.body.style.overflow = 'hidden';

            // Ouvrir le modal automatiquement
            const bootstrap = (window as any).bootstrap;
            if (bootstrap) {
                const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
                modal.show();
            }

            return () => {
                modalElement.removeEventListener('hidden.bs.modal', handleModalHidden);
                document.body.style.overflow = '';
            };
        }
    }, [handleClose]);

    const getPlanBadgeClass = (plan: string): string => {
        switch (plan) {
            case 'FREE': return 'bg-secondary';
            case 'ESSENTIAL': return 'bg-warning';
            case 'PLUS': return 'bg-primary';
            case 'ENTERPRISE': return 'bg-success';
            default: return 'bg-secondary';
        }
    };

    const getPlanTransitionType = (fromPlan: string, toPlan: string): string => {
        const planOrder: Record<string, number> = { FREE: 0, ESSENTIAL: 1, PLUS: 2, ENTERPRISE: 3 };
        if (planOrder[toPlan] > planOrder[fromPlan]) return 'upgrade';
        if (planOrder[toPlan] < planOrder[fromPlan]) return 'downgrade';
        return 'other';
    };

    const getPlanDetails = (plan: string) => {
        const planMap: Record<string, any> = {
            FREE: {
                name: 'FREE',
                emoji: '🆓',
                description: '40 crédits initiaux, mode consultation après épuisement'
            },
            ESSENTIAL: {
                name: 'ESSENTIAL',
                emoji: '⭐',
                description: '40 crédits one-time, $300, top-up disponible, pas d\'expiration'
            },
            PLUS: {
                name: 'PLUS',
                emoji: '💎',
                description: '100 crédits/mois, $500/mois, roll-over 10%, grace period 7j'
            },
            ENTERPRISE: {
                name: 'ENTERPRISE',
                emoji: '🏢',
                description: 'Crédits sur mesure, tarification personnalisée, support dédié'
            }
        };
        return planMap[plan] || { name: plan, emoji: '', description: '' };
    };

    const modalContent = (
        <div
            ref={modalRef}
            className="modal fade"
            data-bs-backdrop="true"
            data-bs-keyboard="true"
            id={id}
            tabIndex={-1}
            aria-labelledby={`${id}Label`}
            aria-hidden="true"
            style={{ zIndex: 10000, position: 'fixed' }}
        >
            <div className="modal-dialog modal-dialog-centered modal-lg" style={{ zIndex: 10001, pointerEvents: 'auto' }}>
                <div className="modal-content" style={{ zIndex: 10002, pointerEvents: 'auto', backgroundColor: '#1a1a1a', borderRadius: '15px' }}>
                    <div className="modal-header border-secondary" style={{ borderRadius: '15px 15px 0 0' }}>
                        <h5 className="modal-title text-theme" id={`${id}Label`}>
                            <i className="fa-solid fa-exchange-alt me-2"></i>
                            {t('views.modal.planChange.title', 'Changer de plan')}
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        ></button>
                    </div>
                    <div className="modal-body text-white p-4">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">{t('views.modal.planChange.loading', 'Chargement des plans disponibles...')}</p>
                            </div>
                        ) : availablePlans ? (
                            <>
                                {/* Plan actuel */}
                                <div className="mb-4 p-3 rounded-3" style={{ background: 'rgba(255,255,255,0.05)' }}>
                                    <h6 className="text-theme mb-3">
                                        <i className="fa-solid fa-info-circle me-2"></i>
                                        {t('views.modal.planChange.currentPlan', 'Plan actuel')}
                                    </h6>
                                    <div className="d-flex align-items-center">
                                        <span className={`badge ${getPlanBadgeClass(currentPlan)} px-3 py-2 fs-6 me-3`}>
                                            {getPlanDetails(currentPlan).emoji} {currentPlan}
                                        </span>
                                        <span className="text-muted">{getPlanDetails(currentPlan).description}</span>
                                    </div>
                                </div>

                                {/* Recommandation backend (si disponible) */}
                                {availablePlans.recommendations && availablePlans.recommendations.length > 0 && (
                                    <div className="alert alert-info mb-4">
                                        <h6 className="mb-2">
                                            <i className="fa-solid fa-lightbulb me-2"></i>
                                            {t('views.modal.planChange.recommendation', 'Recommandations')}
                                        </h6>
                                        <ul className="mb-0 small">
                                            {availablePlans.recommendations.map((rec: any, idx: number) => {
                                                // Les recommendations peuvent être strings ou objets
                                                const recText = typeof rec === 'string' ? rec : (rec.reason || rec.message || JSON.stringify(rec));
                                                return (
                                                    <li key={idx}>{recText}</li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}

                                {/* Plans disponibles */}
                                <h6 className="text-theme mb-3">
                                    <i className="fa-solid fa-list me-2"></i>
                                    {t('views.modal.planChange.availablePlans', 'Plans disponibles')}
                                </h6>
                                <div className="row g-3">
                                    {availablePlans.available_plans.map((planObj: any, idx: number) => {
                                        // Le backend retourne des objets {plan, reason, price, credits, best_for}
                                        const planName = typeof planObj === 'string' ? planObj : planObj.plan;
                                        const details = getPlanDetails(planName);
                                        const isSelected = selectedPlan === planName;
                                        const transitionType = getPlanTransitionType(currentPlan, planName);
                                        
                                        return (
                                            <div key={planName || idx} className="col-12">
                                                <div
                                                    className={`card h-100 ${isSelected ? 'border-primary border-3' : 'border-secondary'}`}
                                                    style={{
                                                        backgroundColor: isSelected ? 'rgba(13, 110, 253, 0.1)' : '#2a2a2a',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s ease',
                                                        borderRadius: '12px'
                                                    }}
                                                    onClick={() => setSelectedPlan(planName)}
                                                    onMouseEnter={(e) => {
                                                        if (!isSelected) {
                                                            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (!isSelected) {
                                                            e.currentTarget.style.backgroundColor = '#2a2a2a';
                                                        }
                                                    }}
                                                >
                                                    <div className="card-body p-3">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div className="d-flex align-items-center">
                                                                <span className={`badge ${getPlanBadgeClass(planName)} px-3 py-2 me-3`}>
                                                                    {details.emoji} {details.name}
                                                                </span>
                                                                {transitionType === 'upgrade' && (
                                                                    <span className="badge bg-success px-2 py-1 small">
                                                                        <i className="fa-solid fa-arrow-up me-1"></i>Upgrade
                                                                    </span>
                                                                )}
                                                                {transitionType === 'downgrade' && (
                                                                    <span className="badge bg-warning text-dark px-2 py-1 small">
                                                                        <i className="fa-solid fa-arrow-down me-1"></i>Downgrade
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {isSelected && (
                                                                <i className="fa-solid fa-check-circle text-primary fs-4"></i>
                                                            )}
                                                        </div>
                                                        <p className="text-muted mb-0 mt-2 small">
                                                            {details.description}
                                                            {/* Afficher la raison du backend si disponible */}
                                                            {typeof planObj === 'object' && planObj.reason && (
                                                                <><br /><span className="text-info">💡 {planObj.reason}</span></>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Message d'information selon transition */}
                                {selectedPlan && (
                                    <div className="mt-4">
                                        {getPlanTransitionType(currentPlan, selectedPlan) === 'downgrade' && 
                                         currentPlan === 'PLUS' && selectedPlan === 'ESSENTIAL' && (
                                            <div className="alert alert-warning">
                                                <h6 className="mb-2">
                                                    <i className="fa-solid fa-exclamation-triangle me-2"></i>
                                                    {t('views.modal.planChange.downgradeWarning', 'Attention - Procédure en 2 étapes')}
                                                </h6>
                                                <p className="mb-0 small">
                                                    {t('views.modal.planChange.plusToEssentialSteps', 
                                                        'Pour passer de PLUS à ESSENTIAL, vous devez d\'abord annuler votre subscription PLUS, puis activer ESSENTIAL.')}
                                                </p>
                                            </div>
                                        )}
                                        {getPlanTransitionType(currentPlan, selectedPlan) === 'upgrade' && (
                                            <div className="alert alert-info">
                                                <i className="fa-solid fa-info-circle me-2"></i>
                                                {t('views.modal.planChange.upgradeInfo', 
                                                    'Vous serez redirigé vers le paiement sécurisé. Le changement sera effectif immédiatement après paiement.')}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="alert alert-danger">
                                {t('views.modal.planChange.error', 'Erreur lors du chargement des plans disponibles')}
                            </div>
                        )}
                    </div>
                    <div className="modal-footer border-secondary">
                        <button
                            ref={closeRef}
                            type="button"
                            className="btn btn-secondary"
                            data-bs-dismiss="modal"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                            disabled={processing}
                        >
                            {t('words.cancel', 'Annuler')}
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirmChange}
                            className="btn btn-primary"
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                            disabled={!selectedPlan || processing || loading}
                        >
                            {processing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {t('views.modal.planChange.processing', 'Traitement...')}
                                </>
                            ) : (
                                <>
                                    <i className="fa-solid fa-check me-2"></i>
                                    {t('views.modal.planChange.confirm', 'Confirmer le changement')}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default PlanChangeModal;
